import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import PhygitalIcon, { PoweredByPhygital } from "@/components/PhygitalIcon";
import {
  Calendar,
  Clock,
  MapPin,
  Check,
  QrCode,
  Wallet,
  User,
  Accessibility,
  Coffee,
  Heart,
  Sparkles,
  AlertCircle,
  Download,
  Share,
  CheckCircle2,
  XCircle,
  Link2,
  Copy,
  Eye
} from "lucide-react";
import { toast } from "sonner";

const EventPassPage = () => {
  const { eventId, passToken } = useParams();
  const navigate = useNavigate();
  const [passData, setPassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkinResult, setCheckinResult] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Format time to 12-hour format
  const formatTime12Hour = (time) => {
    if (!time) return "";
    let hours, minutes;
    if (time.includes(':')) {
      [hours, minutes] = time.split(':').map(Number);
    } else {
      return time;
    }
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${String(minutes).padStart(2, '0')} ${ampm}`;
  };

  useEffect(() => {
    fetchPassData();
  }, [eventId, passToken]);

  const fetchPassData = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/pass/${passToken}`);
      setPassData(res.data);
    } catch (err) {
      console.error("Error fetching pass:", err);
      setError(err.response?.data?.detail || "Pass not found");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async () => {
    setCheckingIn(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/checkin/${passToken}`);
      setCheckinResult(res.data);
    } catch (err) {
      console.error("Check-in error:", err);
      setCheckinResult({ success: false, error: err.response?.data?.detail || "Check-in failed" });
    } finally {
      setCheckingIn(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleShare = async () => {
    const shareData = {
      title: `${passData?.event?.name || "Event"} Pass`,
      text: `My pass for ${passData?.event?.name}`,
      url: window.location.href
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        copyLink();
      }
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Invalid Pass</h1>
          <p className="text-gray-600 text-sm mb-6">{error}</p>
          <Button 
            onClick={() => navigate("/")}
            className="bg-gray-900 text-white hover:bg-gray-800"
          >
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const { pass, event, preferences_summary } = passData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="max-w-md mx-auto px-6 py-8">
        
        {/* Pass Card - Light Mode */}
        <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-lg">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-xs uppercase tracking-wider">Event Pass</p>
                <h1 className="text-white font-semibold text-lg">{event?.name}</h1>
              </div>
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
          
          {/* Guest Info */}
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                <User className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-gray-900 font-medium">{pass?.guest_name}</p>
                <p className="text-gray-600 text-sm">{pass?.guest_email}</p>
              </div>
            </div>
          </div>
          
          {/* QR Code Section - Light Background */}
          <div className="p-5 flex flex-col items-center bg-gray-50">
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-3">
              {pass?.qr_code ? (
                <img 
                  src={pass.qr_code} 
                  alt="Pass QR Code" 
                  className="w-52 h-52"
                />
              ) : (
                <div className="w-52 h-52 flex items-center justify-center">
                  <QrCode className="w-20 h-20 text-gray-300" />
                </div>
              )}
            </div>
            <p className="text-gray-700 font-mono text-sm font-medium">{pass?.pass_token}</p>
            <p className="text-gray-500 text-xs mt-1">Show this at entry • NFC backup available</p>
          </div>
          
          {/* Event Details */}
          <div className="px-5 py-4 border-t border-gray-100">
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-gray-700">
                <Calendar className="w-4 h-4 text-purple-500" />
                <span className="text-sm">{event?.date}</span>
              </div>
              {event?.start_time && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Clock className="w-4 h-4 text-purple-500" />
                  <span className="text-sm">{formatTime12Hour(event?.start_time)}</span>
                </div>
              )}
              {event?.venue_name && (
                <div className="flex items-center gap-3 text-gray-700">
                  <MapPin className="w-4 h-4 text-purple-500" />
                  <span className="text-sm">{event?.venue_name}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Preferences Summary */}
          {(preferences_summary?.accessibility?.length > 0 || 
            preferences_summary?.dietary?.length > 0 || 
            preferences_summary?.vibe) && (
            <div className="px-5 pb-4">
              <p className="text-gray-500 text-xs uppercase tracking-wider mb-3">Your Preferences</p>
              <div className="flex flex-wrap gap-2">
                {preferences_summary?.accessibility?.map((item) => (
                  <span 
                    key={item}
                    className="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs rounded-full flex items-center gap-1 border border-blue-100"
                  >
                    <Accessibility className="w-3 h-3" />
                    {item.replace(/_/g, ' ')}
                  </span>
                ))}
                {preferences_summary?.dietary?.map((item) => (
                  <span 
                    key={item}
                    className="px-3 py-1.5 bg-green-50 text-green-700 text-xs rounded-full flex items-center gap-1 border border-green-100"
                  >
                    <Coffee className="w-3 h-3" />
                    {item.replace(/_/g, ' ')}
                  </span>
                ))}
                {preferences_summary?.vibe && (
                  <span className="px-3 py-1.5 bg-amber-50 text-amber-700 text-xs rounded-full flex items-center gap-1 border border-amber-100">
                    <Sparkles className="w-3 h-3" />
                    {preferences_summary.vibe.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Status Badge */}
          <div className="px-5 pb-5">
            {pass?.checked_in ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-green-700 font-medium text-sm">Checked In</p>
                  <p className="text-green-600/70 text-xs">
                    {pass.checked_in_at && new Date(pass.checked_in_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="text-amber-700 font-medium text-sm">Not Yet Checked In</p>
                  <p className="text-amber-600/70 text-xs">Show QR code at entry</p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Check-in Result Modal */}
        {checkinResult && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center shadow-xl">
              {checkinResult.success ? (
                <>
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-gray-900 font-semibold text-lg mb-2">
                    {checkinResult.already_checked_in ? "Already Checked In" : "Check-in Successful!"}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Welcome, {checkinResult.guest_name}!
                  </p>
                  
                  {/* Show preferences for staff */}
                  {checkinResult.preferences && (
                    <div className="bg-gray-50 rounded-xl p-4 text-left mb-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Eye className="w-4 h-4 text-gray-600" />
                        <p className="text-gray-600 text-xs uppercase font-medium">Guest Needs</p>
                      </div>
                      <div className="space-y-2">
                        {checkinResult.preferences.accessibility?.length > 0 && (
                          <p className="text-sm text-blue-700 flex items-center gap-1">
                            <Accessibility className="w-3 h-3" />
                            {checkinResult.preferences.accessibility.join(", ")}
                          </p>
                        )}
                        {checkinResult.preferences.dietary?.length > 0 && (
                          <p className="text-sm text-green-700 flex items-center gap-1">
                            <Coffee className="w-3 h-3" />
                            {checkinResult.preferences.dietary.join(", ")}
                          </p>
                        )}
                        {checkinResult.preferences.invisible_disabilities?.length > 0 && (
                          <p className="text-sm text-pink-700 flex items-center gap-1">
                            <Heart className="w-3 h-3" />
                            {checkinResult.preferences.invisible_disabilities.join(", ")}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                    <XCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-gray-900 font-semibold text-lg mb-2">Check-in Failed</h3>
                  <p className="text-gray-600 text-sm mb-4">{checkinResult.error}</p>
                </>
              )}
              
              <Button 
                onClick={() => {
                  setCheckinResult(null);
                  if (checkinResult.success) fetchPassData();
                }}
                className="w-full bg-gray-900 text-white hover:bg-gray-800"
              >
                Close
              </Button>
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <Button 
            onClick={() => {
              const link = document.createElement('a');
              link.href = pass?.qr_code;
              link.download = `pass-${pass?.pass_token}.png`;
              link.click();
            }}
            variant="outline"
            className="border-gray-200 text-gray-700 py-5"
          >
            <Download className="w-4 h-4 mr-2" />
            Save QR
          </Button>
          <Button 
            onClick={copyLink}
            variant="outline"
            className="border-gray-200 text-gray-700 py-5"
          >
            {linkCopied ? (
              <Check className="w-4 h-4 mr-2 text-green-600" />
            ) : (
              <Link2 className="w-4 h-4 mr-2" />
            )}
            {linkCopied ? "Copied!" : "Copy Link"}
          </Button>
        </div>
        
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Button 
            onClick={handleShare}
            variant="outline"
            className="border-gray-200 text-gray-700 py-5"
          >
            <Share className="w-4 h-4 mr-2" />
            Share
          </Button>
          {!pass?.checked_in && (
            <Button 
              onClick={handleCheckin}
              disabled={checkingIn}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-5"
            >
              {checkingIn ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Check In
            </Button>
          )}
        </div>
        
        {/* Footer */}
        <footer className="mt-8 text-center">
          <PoweredByPhygital />
        </footer>
      </div>
    </div>
  );
};

export default EventPassPage;
